import { useCallback, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'
import { useHostContext } from '@/contexts/HostContext'
import {
  nextMessagesAfterSend,
  messagesAfterActionResolution,
  statusFromError,
  type CopilotAction,
  type CopilotMessage,
  type CopilotExecutedAction,
  type CopilotPendingAction,
  type CopilotRole,
  type CopilotStatus,
} from '@/hooks/copilotChatLogic'

export type { CopilotAction, CopilotExecutedAction, CopilotMessage, CopilotPendingAction, CopilotRole, CopilotStatus }

/**
 * Copilot Sales (MASTER.md AA-16) — chat hook. Follows the same hand-rolled
 * mutation pattern as useRelationships.ts's createPersona: no cache, no
 * react-query (unused in this repo), local state + ApiError on failure.
 *
 * The client resends the full message history on every call (server holds
 * no session state — api/src/copilot.js), which keeps the backend stateless
 * and is simple enough for a first version's conversation lengths.
 *
 * Persistence: the conversation is mirrored to localStorage, keyed per
 * signed-in account, so it survives closing the panel or reloading the
 * page. This is purely a client-side convenience — it lives only in the
 * user's own browser profile (never sent anywhere new, never shared with
 * the team) and the user can wipe it at any time with clearConversation().
 */

interface CopilotChatState {
  messages: CopilotMessage[]
  status: CopilotStatus
  error: ApiError | Error | null
  sendMessage: (text: string) => Promise<boolean>
  resolveAction: (actionId: string, decision: 'confirmar' | 'cancelar') => Promise<void>
  clearConversation: () => void
}

const STORAGE_PREFIX = 'wacner-copilot-chat'
const MAX_STORED_MESSAGES = 40

function loadStored(key: string): CopilotMessage[] {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveStored(key: string, messages: CopilotMessage[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)))
  } catch {
    // Storage full or unavailable (private browsing, etc.) — the
    // conversation still works in-memory for the current session.
  }
}

export function useCopilotChat(): CopilotChatState {
  const { gate, makeClient } = useApiClient()
  const { host } = useHostContext()
  const { accounts } = useMsal()

  const storageKey = `${STORAGE_PREFIX}:${host === 'teams' ? 'teams' : accounts[0]?.homeAccountId ?? 'anonymous'}`
  const storageKeyRef = useRef(storageKey)
  storageKeyRef.current = storageKey

  const [messages, setMessages] = useState<CopilotMessage[]>(() => loadStored(storageKey))
  const [status, setStatus] = useState<CopilotStatus>('idle')
  const [error, setError] = useState<ApiError | Error | null>(null)

  // Re-load if the signed-in account changes (e.g. account switch) instead
  // of silently showing the previous account's conversation.
  const loadedKeyRef = useRef(storageKey)
  useEffect(() => {
    if (loadedKeyRef.current === storageKey) return
    loadedKeyRef.current = storageKey
    setMessages(loadStored(storageKey))
  }, [storageKey])

  useEffect(() => {
    saveStored(storageKeyRef.current, messages)
  }, [messages])

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return false

      const client = makeClient()
      if (gate !== 'ready' || !client) {
        setStatus('not_signed_in')
        return false
      }

      // previousMessages (not nextMessages) is what a failed send rolls
      // back to: nextMessages is only committed to state once the request
      // actually succeeds, so a failed turn never lingers in history to be
      // silently resent — and re-sending the same text is then a clean
      // retry, not a duplicate.
      const previousMessages = messages
      const nextMessages: CopilotMessage[] = [...messages, { role: 'user', content: trimmed }]
      setMessages(nextMessages)
      setStatus('sending')
      setError(null)

      try {
        const response = await client.postOnce<{ reply: string; actions: CopilotAction[]; pendingActions?: CopilotPendingAction[]; executedActions?: CopilotExecutedAction[] }>('/api/copilot/chat', {
          messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
        })
        setMessages(nextMessagesAfterSend(previousMessages, nextMessages, { ok: true, reply: response.reply, actions: response.actions, pendingActions: response.pendingActions, executedActions: response.executedActions }))
        setStatus('idle')
        return true
      } catch (err) {
        setMessages(nextMessagesAfterSend(previousMessages, nextMessages, { ok: false }))
        setError(err as ApiError | Error)
        setStatus(statusFromError(err))
        return false
      }
    },
    [makeClient, gate, messages]
  )

  /**
   * Confirms or cancels a Nivel B pending action (MASTER.md AA-20). Only
   * this explicit user gesture — never the model — can execute an outward
   * operation; the backend additionally verifies owner, expiry and
   * single-use before touching Microsoft 365.
   */
  const resolveAction = useCallback(
    async (actionId: string, decision: 'confirmar' | 'cancelar') => {
      const client = makeClient()
      if (gate !== 'ready' || !client) {
        setStatus('not_signed_in')
        return
      }
      try {
        const response = await client.post<{ ejecutada?: boolean; cancelada?: boolean; resumen?: string; executedAction?: CopilotExecutedAction }>('/api/copilot/confirm-action', {
          actionId,
          decision,
        })
        const estado = response.ejecutada ? 'ejecutada' : 'cancelada'
        setMessages((prev) => messagesAfterActionResolution(prev, actionId, estado,
          response.ejecutada
            ? `Ejecutada y verificada en Microsoft 365${response.executedAction?.id ? ` — id ${response.executedAction.id}` : ''}.`
            : 'Cancelada — no se envió nada.'))
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null
        const estado = apiError?.message === 'ACTION_EXPIRED' ? 'expirada' : apiError?.message === 'ACTION_ALREADY_USED' ? 'ejecutada' : 'error'
        setMessages((prev) => messagesAfterActionResolution(prev, actionId, estado,
          estado === 'expirada' ? 'La confirmación venció — pídele a Copilot que la prepare de nuevo.'
            : estado === 'ejecutada' ? 'Esta acción ya había sido resuelta.'
              : `No se pudo ejecutar.${apiError?.correlationId ? ` Código de referencia: ${apiError.correlationId}` : ''}`))
      }
    },
    [makeClient, gate]
  )

  const clearConversation = useCallback(() => {
    setMessages([])
    setError(null)
    try {
      window.localStorage.removeItem(storageKeyRef.current)
    } catch {
      // ignore
    }
  }, [])

  return { messages, status, error, sendMessage, resolveAction, clearConversation }
}
